<div class="flex flex-col gap-4 p-4">
  <!-- Header -->
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-default text-xl font-bold">System Admin Dashboard</h1>
      <p class="text-secondary text-sm">Tenant overview, user activity, system health and security monitoring</p>
    </div>
  </div>

  <!-- Filter Bar -->
  <div class="rounded-xl border bg-white px-4 py-1">
    <app-global-filter-panel [config]="filterConfig" (filtersChanged)="onFiltersChanged($event)" />
  </div>

  <!-- Tenant KPIs -->
  <app-kpi-card-grid [cards]="tenantKpis" title="PLATFORM OVERVIEW" [columns]="6" />

  <!-- Tenant detail card -->
  @if (!hasUsersPermission) {
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
        <mat-icon class="!text-base text-red-400">lock</mat-icon>
        Permissions Restricted
      </div>
    </div>
  } @else if (tenantData) {
    <div class="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
      <div class="flex flex-wrap items-center gap-2">
        <!-- Company name -->
        <div class="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5">
          <mat-icon class="!text-sm text-gray-500">business</mat-icon>
          <span class="text-[11px] font-medium text-gray-500">Company</span>
          <span class="text-xs font-bold text-gray-800">{{ tenantData.company_name }}</span>
        </div>
        <!-- Subscription -->
        <div class="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5">
          <mat-icon class="!text-sm text-gray-500">subscriptions</mat-icon>
          <span class="text-[11px] font-medium text-gray-500">Subscription</span>
          <span class="rounded-full px-2 py-0.5 text-[10px] font-bold capitalize" [ngClass]="getSubscriptionStatusColor(tenantData.subscription_status)">
            {{ tenantData.subscription_status || 'N/A' }}
          </span>
        </div>
        <!-- License Expiry -->
        <div class="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5">
          <mat-icon class="!text-sm text-gray-500">verified</mat-icon>
          <span class="text-[11px] font-medium text-gray-500">License Expiry</span>
          <span class="text-xs font-bold" [ngClass]="tenantData.license_expiry ? 'text-gray-800' : 'text-gray-400'">
            {{ tenantData.license_expiry ? (tenantData.license_expiry | date: 'mediumDate') : 'N/A' }}
          </span>
        </div>
        <!-- Mollak -->
        <div class="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5">
          <mat-icon class="!text-sm" [class.text-green-500]="tenantData.mollak_enabled" [class.text-gray-500]="!tenantData.mollak_enabled">cloud_sync</mat-icon>
          <span class="text-[11px] font-medium text-gray-500">Mollak</span>
          <span
            class="rounded-full px-2 py-0.5 text-[10px] font-bold"
            [class.bg-green-100]="tenantData.mollak_enabled"
            [class.text-green-700]="tenantData.mollak_enabled"
            [class.bg-gray-100]="!tenantData.mollak_enabled"
            [class.text-gray-500]="!tenantData.mollak_enabled"
          >
            {{ tenantData.mollak_enabled ? 'Enabled' : 'Disabled' }}
          </span>
        </div>
        <!-- Inspections -->
        <div class="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5">
          <mat-icon class="!text-sm" [class.text-blue-500]="tenantData.inspections_enabled" [class.text-gray-500]="!tenantData.inspections_enabled">search</mat-icon>
          <span class="text-[11px] font-medium text-gray-500">Inspections</span>
          <span
            class="rounded-full px-2 py-0.5 text-[10px] font-bold"
            [class.bg-blue-100]="tenantData.inspections_enabled"
            [class.text-blue-700]="tenantData.inspections_enabled"
            [class.bg-gray-100]="!tenantData.inspections_enabled"
            [class.text-gray-500]="!tenantData.inspections_enabled"
          >
            {{ tenantData.inspections_enabled ? 'Enabled' : 'Disabled' }}
          </span>
        </div>
      </div>
    </div>
  }

  <!-- Row: User Activity + Security Status (comparable heights) -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <!-- User Activity -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div class="mb-3 flex items-center gap-2">
        <div class="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50">
          <mat-icon class="!text-sm text-blue-500">group</mat-icon>
        </div>
        <h3 class="text-default text-sm font-bold">User Activity</h3>
      </div>
      @if (!hasUsersPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingUserActivity) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (userActivityData) {
        <!-- 3 KPI rows -->
        <div class="mb-3 flex flex-col gap-1.5">
          <div class="flex items-center gap-3 rounded-lg border border-l-4 border-gray-100 border-l-blue-400 bg-white px-3 py-2 shadow-sm">
            <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-50">
              <mat-icon class="!text-sm text-blue-500">people</mat-icon>
            </div>
            <span class="flex-1 text-xs font-semibold text-gray-700">Total Users</span>
            <span class="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold" [ngClass]="userActivityData.total_users ?? 0 | countBadge">{{ userActivityData.total_users ?? 0 }}</span>
          </div>
          <div class="flex items-center gap-3 rounded-lg border border-l-4 border-gray-100 border-l-green-500 bg-white px-3 py-2 shadow-sm">
            <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-green-50">
              <mat-icon class="!text-sm text-green-500">check_circle</mat-icon>
            </div>
            <span class="flex-1 text-xs font-semibold text-gray-700">Active</span>
            <span class="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold" [ngClass]="userActivityData.active_users ?? 0 | countBadge">{{ userActivityData.active_users ?? 0 }}</span>
          </div>
          <div class="flex items-center gap-3 rounded-lg border border-l-4 border-gray-100 border-l-red-500 bg-white px-3 py-2 shadow-sm">
            <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-red-50">
              <mat-icon class="!text-sm text-red-500">block</mat-icon>
            </div>
            <span class="flex-1 text-xs font-semibold text-gray-700">Blocked</span>
            <span class="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold" [ngClass]="userActivityData.blocked_users ?? 0 | countBadge">{{ userActivityData.blocked_users ?? 0 }}</span>
          </div>
        </div>

        @if (byRoleItems.length > 0) {
          <div class="mb-2 flex items-center gap-2">
            <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">By Role</span>
            <div class="flex-1 border-t border-gray-100"></div>
          </div>
          <div class="mb-3">
            <app-icon-count-list [items]="byRoleItems" />
          </div>
        }

        @if (topUserItems.length > 0) {
          <div class="mb-2 flex items-center gap-2">
            <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">Most Active</span>
            <div class="flex-1 border-t border-gray-100"></div>
          </div>
          <app-icon-count-list [items]="topUserItems" />
        }
      }
    </div>

    <!-- Security Status -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div class="mb-3 flex items-center gap-2">
        <div class="flex h-7 w-7 items-center justify-center rounded-lg bg-red-50">
          <mat-icon class="!text-sm text-red-500">security</mat-icon>
        </div>
        <h3 class="text-default text-sm font-bold">Security Status</h3>
      </div>
      @if (!hasUsersPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingSecurity) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (securityData) {
        <!-- Security KPI row -->
        <div class="mb-3 flex flex-col gap-1.5">
          <div class="flex items-center gap-3 rounded-lg border border-l-4 border-gray-100 border-l-orange-400 bg-white px-3 py-2.5 shadow-sm">
            <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-50">
              <mat-icon class="!text-sm text-orange-500">lock_clock</mat-icon>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs font-semibold text-gray-800">Stale Passwords</p>
              <p class="text-[10px] text-gray-400">Not changed in &gt;90 days</p>
            </div>
            <span class="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold" [ngClass]="securityData.password_expiring ?? 0 | countBadge">{{ securityData.password_expiring ?? 0 }}</span>
          </div>
        </div>

        @if (securityData.recently_blocked?.length > 0) {
          <div class="mb-2 flex items-center gap-2">
            <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">Recently Blocked</span>
            <div class="flex-1 border-t border-gray-100"></div>
          </div>
          <div class="flex flex-col gap-1">
            @for (u of securityData.recently_blocked; track $index) {
              <div class="flex items-center gap-2.5 overflow-hidden rounded-lg border border-l-4 border-gray-100 border-l-red-500 bg-white py-2 pl-2.5 pr-2.5 shadow-sm">
                <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-50 text-[10px] font-bold text-red-600">
                  {{ (u.name || '').substring(0, 2).toUpperCase() }}
                </div>
                <div class="min-w-0 flex-1">
                  <p class="truncate text-xs font-semibold text-gray-800">{{ u.name }}</p>
                  <p class="truncate text-[10px] text-gray-400">{{ u.email }}</p>
                </div>
                <span class="shrink-0 text-[10px] text-gray-400">{{ u.updated_at | date: 'mediumDate' }}</span>
              </div>
            }
          </div>
        }
      }
    </div>
  </div>

  <!-- System & Module Health -->
  <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <div class="mb-3">
      <h3 class="text-sm font-semibold text-gray-900">System &amp; Module Health</h3>
      <p class="text-secondary pt-0.5 text-xs">Module Activity (Period)</p>
    </div>

    @if (!hasUsersPermission) {
      <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
        <mat-icon class="!text-base text-red-400">lock</mat-icon>
        Permissions Restricted
      </div>
    } @else if (loadingHealth) {
      <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
    } @else if (moduleUsageItems.length > 0) {
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        @for (m of moduleUsageItems; track m.key) {
          <div class="rounded-lg border border-gray-100 bg-gray-50 p-3 text-center">
            <div class="mb-1 flex items-center justify-center gap-1.5">
              <mat-icon class="text-gray-500 icon-size-4">{{ m.icon }}</mat-icon>
              <span class="text-secondary text-[10px] font-semibold uppercase tracking-wide">{{ m.label }}</span>
            </div>
            <div class="text-xl font-bold text-gray-800">{{ m.count }}</div>
          </div>
        }
      </div>
    } @else {
      <div class="text-secondary flex flex-col items-center gap-2 py-6">
        <mat-icon>info</mat-icon>
        <span class="text-sm">No data available</span>
      </div>
    }
  </div>

  <!-- Subscription Billing Summary -->
  <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <app-subscription-billing-card [dashboardType]="dashboardType" [filters]="filters" [requiredPermission]="'subscription_bills_view'" />
  </div>

  <!-- Bottom Row: Timeline -->
  <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <app-activity-timeline [requiredPermission]="'activity_logs_other'" />
  </div>
</div>
